/**
 * A dica flutuante: um só para o software inteiro.
 *
 * ═══ O QUE ESTAVA ACONTECENDO ═══
 *
 * O código usa `data-dica` em dezenas de lugares — botões de zoom,
 * chaves de câmera, chips de classe, o selo do objeto. Todas escritas,
 * todas MUDAS: a versão anterior tinha o JavaScript que as desenhava, e
 * ele não foi migrado. O atributo ficou no HTML sem ninguém ler.
 *
 * ═══ POR QUE UM SÓ, E NÃO UM POR COMPONENTE ═══
 *
 * Porque a dica precisa escapar da caixa onde nasce. Dentro de um cartão
 * com `overflow: hidden` — que é a maioria — ela seria cortada. Aqui ela
 * mora no corpo da página, posicionada em pixels da janela, e ouve o
 * mouse por delegação: qualquer elemento com `data-dica`, presente ou
 * futuro, ganha dica sem precisar saber que ela existe.
 *
 * ═══ TÍTULO NATIVO NÃO SERVE ═══
 *
 * O `title` do navegador demora ~1,5 s para aparecer, não aceita duas
 * linhas e some ao mexer o mouse. Para explicar o que um ícone faz, esse
 * atraso é o suficiente para a pessoa desistir e clicar para descobrir.
 */

import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { posicaoDaDica } from '../paleta'

export function Dica() {
  const [texto, setTexto] = useState<string | null>(null)
  const [pos, setPos] = useState({ topo: 0, esquerda: 0, embaixo: false })
  const caixa = useRef<HTMLDivElement>(null)
  const alvo = useRef<HTMLElement | null>(null)

  useEffect(() => {
    const entrou = (e: MouseEvent) => {
      const el = (e.target as HTMLElement | null)
        ?.closest<HTMLElement>('[data-dica]')
      if (!el) { alvo.current = null; setTexto(null); return }
      if (el === alvo.current) return
      alvo.current = el
      setTexto(el.dataset.dica ?? null)
    }
    document.addEventListener('mouseover', entrou)
    // Rolar com a dica aberta a deixaria flutuando longe do elemento.
    const sumir = () => { alvo.current = null; setTexto(null) }
    window.addEventListener('scroll', sumir, true)
    return () => {
      document.removeEventListener('mouseover', entrou)
      window.removeEventListener('scroll', sumir, true)
    }
  }, [])

  /* A posição só dá para calcular DEPOIS de o texto estar na tela: é
     preciso saber a altura dele, e ela depende de quantas linhas o texto
     ocupou. Por isso mede aqui, e não no evento do mouse. */
  useEffect(() => {
    if (!texto || !alvo.current || !caixa.current) return
    const a = alvo.current.getBoundingClientRect()
    const d = caixa.current.getBoundingClientRect()
    setPos(posicaoDaDica(
      { top: a.top, bottom: a.bottom, left: a.left, width: a.width },
      { largura: d.width, altura: d.height },
      { largura: window.innerWidth, altura: window.innerHeight }))
  }, [texto])

  if (!texto) return null

  return createPortal(
    <div ref={caixa}
         className={'dica-flutuante on' + (pos.embaixo ? ' embaixo' : '')}
         style={{ top: pos.topo, left: pos.esquerda }}>
      {texto}
    </div>,
    document.body)
}
